import { useMemo, useState } from 'react';
import {
	Ban,
	Clock3,
	Eye,
	MessageCircle,
	Send,
	TriangleAlert,
} from 'lucide-react';
import { WhatsAppMessagePreview } from '@/components/whatsapp/WhatsAppMessagePreview';
import { cn } from '@/lib/utils';
import {
	formatSessionWhatsAppMessagePreview,
	formatSessionWhatsAppRelevantTimestamp,
	SESSION_WHATSAPP_STATUS_LABEL,
	SESSION_WHATSAPP_STATUS_STYLES,
	SESSION_WHATSAPP_TYPE_LABEL,
} from '@/utils/sessionWhatsAppDisplay';

type SessionWhatsAppNotificationsPanelProps = {
	notifications: SessionWhatsApp.SessionWhatsAppNotification[];
	patientName?: string;
	patientPhone?: string | null;
	className?: string;
	embedded?: boolean;
};

const SESSION_WHATSAPP_STATUS_ICON: Record<
	SessionWhatsApp.SessionWhatsAppNotificationStatus,
	typeof Clock3
> = {
	QUEUED: Clock3,
	SENT: Send,
	DELIVERED: Send,
	READ: Send,
	FAILED: TriangleAlert,
	CANCELLED: Ban,
};

const SessionWhatsAppNotificationsPanel = ({
	notifications,
	patientName,
	patientPhone,
	className,
	embedded = false,
}: SessionWhatsAppNotificationsPanelProps) => {
	const [previewNotification, setPreviewNotification] =
		useState<SessionWhatsApp.SessionWhatsAppNotification | null>(null);

	const sortedNotifications = useMemo(
		() =>
			[...notifications].sort(
				(a, b) =>
					new Date(a.scheduled_for).getTime() -
					new Date(b.scheduled_for).getTime(),
			),
		[notifications],
	);

	const rootClass = embedded
		? cn('space-y-3', className)
		: cn(
				'rounded-2xl border border-emerald-100 bg-white p-4 md:p-5',
				className,
		  );

	const header = (
		<div className={cn('flex items-center gap-2', !embedded && 'mb-4')}>
			<MessageCircle
				className="size-4 text-zinc-500"
				strokeWidth={2.25}
			/>
			<h3 className="text-sm font-semibold text-zinc-500">
				Notificações WhatsApp
			</h3>
		</div>
	);
	if (sortedNotifications.length === 0) {
		return (
			<div className={rootClass}>
				{header}
				<p className="text-xs leading-relaxed text-zinc-500">
					Nenhuma notificação agendada para esta sessão. Verifique se
					o paciente tem notificações habilitadas e telefone com 11
					dígitos.
				</p>
			</div>
		);
	}

	return (
		<>
			<div className={rootClass}>
				{header}
				<ul className="space-y-2.5">
					{sortedNotifications.map((notification) => {
						const relevantTimestampLabel =
							formatSessionWhatsAppRelevantTimestamp(
								notification,
							);
						const StatusIcon =
							SESSION_WHATSAPP_STATUS_ICON[notification.status];

						return (
							<li
								key={notification.id}
								className="space-y-2 rounded-xl border border-zinc-100 bg-gradient-to-br from-white to-zinc-50/90 p-3.5 shadow-sm"
							>
								<div className="flex items-center justify-between gap-3">
									<div className="flex min-w-0 items-center gap-2">
										<p className="font-albert text-sm font-medium text-zinc-600">
											{
												SESSION_WHATSAPP_TYPE_LABEL[
													notification.type
												]
											}
										</p>

										<span className="text-[10px] font-light text-zinc-400">
											{relevantTimestampLabel}
										</span>
									</div>
									<span
										className={cn(
											'inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide',
											SESSION_WHATSAPP_STATUS_STYLES[
												notification.status
											],
										)}
									>
										<StatusIcon
											className="size-3 shrink-0"
											strokeWidth={2.5}
										/>
										{
											SESSION_WHATSAPP_STATUS_LABEL[
												notification.status
											]
										}
									</span>
								</div>
								{notification.rendered_body.trim() ? (
									<p className="font-albert text-[11px] font-light italic leading-snug text-zinc-400">
										{formatSessionWhatsAppMessagePreview(
											notification.rendered_body,
										)}
									</p>
								) : null}
								<div className="flex justify-end">
									<button
										type="button"
										onClick={() =>
											setPreviewNotification(notification)
										}
										className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600/80 underline-offset-2 transition hover:text-emerald-700 hover:underline"
									>
										<Eye size={13} />
										Ver mensagem
									</button>
								</div>
							</li>
						);
					})}
				</ul>
			</div>

			<WhatsAppMessagePreview
				isOpen={previewNotification != null}
				onClose={() => setPreviewNotification(null)}
				message={previewNotification?.rendered_body ?? ''}
				patientName={patientName}
				patientPhone={
					previewNotification?.phone_snapshot ?? patientPhone ?? null
				}
				notification={previewNotification}
			/>
		</>
	);
};

export { SessionWhatsAppNotificationsPanel };
